import { Box, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useLocales } from 'src/locales';
import CiCard from './CiCard';
import { domainFavicon } from '../utils';

export interface TopCompetitorStat {
  domain: string;
  paidPresenceRate: number;
}

interface TopCompetitorsCardProps {
  title: string;
  dateRange: string;
  mainContent?: React.ReactNode;
  competitors?: TopCompetitorStat[];
  loading?: boolean;
}

const DEFAULT_COMPETITORS: TopCompetitorStat[] = [
 ];

function BrandFavicon({ brandDomain, size }: { brandDomain: string; size: number }) {
  
  useEffect(() => {
  }, [brandDomain]);
  
  if (!brandDomain) {
    return null;
  }
  
  const faviconUrl = domainFavicon(brandDomain);
  
  return (
    <Box
      component="img"
      alt={brandDomain}
      src={faviconUrl}
      onError={(e) => console.error('[BrandFavicon] Image failed to load for:', brandDomain, 'URL:', faviconUrl, 'Error:', e)}
      sx={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        backgroundColor: '#fff',
        borderRadius: '50%',
        display: 'block',
        objectFit: 'contain',
        flexShrink: 0,
        border: '1px solid rgba(145, 158, 171, 0.2)',
        padding: '1px',
      }}
    />
  );
}

function TopCompetitorBar(value: number, domain: string) {
  const width = value * 5;
  // Clean domain for display (remove protocol, www, trailing slashes)
  const cleanDomain = domain
    ?.replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
    .trim() || domain;
  
  return (
    <Box
      sx={{
        background: 'rgba(145, 158, 171, 0.20)',
        width: `${width}px`,
        height: '26px',
        borderRadius: '6px',
        paddingY: '4px',
        paddingX: '6px',
      }}
    >
      <Stack direction="row" gap="14px" alignItems="center">
        <Typography fontSize="13px">{value}%</Typography>
        {(() => <BrandFavicon brandDomain={domain} size={20} />)()}
        <Typography fontSize="13px" sx={{ minWidth: 0, flex: 1 }}>
          {cleanDomain}
        </Typography>
      </Stack>
    </Box>
  );
}

export default function TopCompetitorsCard({ competitors, loading, ...props }: TopCompetitorsCardProps) {
  const { t } = useLocales();
  const displayCompetitors = (competitors && competitors.length > 0 ? competitors : DEFAULT_COMPETITORS).slice(0, 3);
  
  const mainContent = displayCompetitors.length > 0 ? (
    <Stack direction="column" gap="10px" marginY="30px">
      {displayCompetitors.map((competitor) => (
        <Box key={competitor.domain}>
          {TopCompetitorBar(Math.round(competitor.paidPresenceRate), competitor.domain)}
        </Box>
      ))}
    </Stack>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '120px',
        marginY: '30px',
        color: 'text.secondary',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
        {t('dashboard.global.no_data_available')}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
        {t('dashboard.global.competitiveIntelligence.no_results_found_description')}
      </Typography>
    </Box>
  );

  return (
    <CiCard
      {...props}
      mainContent={mainContent}
      loading={loading}
      subTitle={t('dashboard.competitiveIntelligence.paidPresenceRate')}
    />
  );
}
